'use client';

import { useCallback, useMemo, useState } from 'react';

import type { FilterConfig, FilterObject, FilterOptions } from '@/types/filters';
import { FilterType } from '@/types/filters';

/**
 * Helper function to transform filters to API format
 * Moved outside hook to avoid dependency issues
 */
function transformFiltersToApi<T extends Record<string, unknown>>(
  filterValues: FilterOptions<T>,
  config: FilterConfig<T>,
): FilterObject[] {
  const result: FilterObject[] = [];

  Object.entries(filterValues).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    const filterCfg = config[key as keyof T];
    if (filterCfg !== undefined) {
      result.push({
        filterKey: filterCfg.filterKey,
        filterType: filterCfg.filterType,
        filterValues: filterCfg.mapToValues(value as NonNullable<T[keyof T]>),
      });
    }
  });

  return result;
}

/**
 * Utility function to create filter configuration with less boilerplate
 *
 * @example
 * // Define your filter options type
 * type MyFilterOptions = {
 *   status?: 'active' | 'inactive';
 *   isVerified?: boolean;
 * };
 *
 * // Create config with simplified format
 * const myFilterConfig = createFilterConfig<MyFilterOptions>({
 *   status: {
 *     filterKey: 'status',
 *     mapToValues: value => [value]
 *   },
 *   isVerified: {
 *     filterKey: 'verified',
 *     mapToValues: () => ['true']
 *   }
 * });
 */
export function createFilterConfig<T extends Record<string, unknown>>(config: {
  [K in keyof T]?: {
    filterKey: string;
    filterType?: FilterType;
    mapToValues: (value: NonNullable<T[K]>) => string[];
  };
}): FilterConfig<T> {
  const filterConfig: FilterConfig<T> = {} as FilterConfig<T>;

  Object.entries(config).forEach(([key, value]) => {
    if (value !== undefined) {
      filterConfig[key as keyof T] = {
        filterKey: value.filterKey,
        filterType: value.filterType || FilterType.INCLUDING,
        mapToValues: value.mapToValues,
      };
    }
  });

  return filterConfig;
}

/**
 * Hook for managing filters with type safety
 * @param initialFilters - Initial filter values
 * @param filterConfig - Configuration for transforming filters to API format
 * @returns Object with filters, API-ready filters, and filter management functions
 */
export default function useFilters<T extends Record<string, unknown>>(
  initialFilters: FilterOptions<T> = {},
  filterConfig: FilterConfig<T>,
) {
  // Internal filter state
  const [filters, setFilters] = useState<FilterOptions<T>>(initialFilters);

  // Convert internal filters to API-ready format
  const apiFilters = useMemo((): FilterObject[] => {
    const filterObjects: FilterObject[] = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }

      const config = filterConfig[key as keyof T];
      if (config !== undefined) {
        filterObjects.push({
          filterKey: config.filterKey,
          filterType: config.filterType,
          filterValues: config.mapToValues(value as NonNullable<T[keyof T]>),
        });
      }
    });

    return filterObjects;
  }, [filters, filterConfig]);

  // Number of active filters
  const activeFilterCount = useMemo((): number => {
    return Object.values(filters).filter(value => value !== undefined).length;
  }, [filters]);

  // Update a single filter value (toggles if same value)
  const setFilter = useCallback(<K extends keyof T>(key: K, value: T[K]): FilterObject[] => {
    setFilters((prev) => {
      const updatedFilters = { ...prev };

      if (prev[key] === value) {
        delete updatedFilters[key];
      } else {
        updatedFilters[key] = value;
      }

      return updatedFilters;
    });

    // Return the updated API filters directly
    const updatedFilters = { ...filters };
    if (filters[key] === value) {
      delete updatedFilters[key];
    } else {
      updatedFilters[key] = value;
    }

    // Transform to API format
    return transformFiltersToApi(updatedFilters, filterConfig);
  }, [filters, filterConfig]);

  // Clear all filters
  const clearFilters = useCallback((): FilterObject[] => {
    setFilters({});
    return [];
  }, []);

  return {
    filters,
    apiFilters,
    setFilter,
    clearFilters,
    activeFilterCount,
  };
}
